import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2, KeyRound } from "lucide-react";

export const ChangePasswordDialog = ({ open, onOpenChange }) => {
  const { t } = useLang();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); setError(""); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 6) { setError(t("profile.tooShort")); return; }
    if (next !== confirm) { setError(t("profile.mismatch")); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success(t("profile.success"));
      reset();
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" /> {t("profile.changePassword")}</DialogTitle>
          <DialogDescription>{t("profile.subtitle")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label>{t("profile.current")}</Label>
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="current-password" autoComplete="current-password" />
            <p className="text-xs text-muted-foreground">{t("profile.currentHint")}</p>
          </div>
          <div className="space-y-2">
            <Label>{t("profile.new")}</Label>
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="new-password" autoComplete="new-password" required />
          </div>
          <div className="space-y-2">
            <Label>{t("profile.confirm")}</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="confirm-password" autoComplete="new-password" required />
          </div>
          {error && <p className="text-sm text-destructive" data-testid="change-password-error">{error}</p>}
          <button type="submit" disabled={saving} data-testid="change-password-submit"
            className="brand-btn flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 font-medium transition-opacity hover:opacity-90 disabled:opacity-60">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("profile.save")}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
